import { API } from '@/modules/api'
import type { TWordwareLunchCartLine } from '@/types/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { defineStore } from 'pinia'
import { formatCurrency } from '@/classes/Helpers'
import { useCartStore } from '@/stores/cart'
import { useStore } from '@/stores'

export const createUniqueId = (familyAccountId: TFamilyAccountId) =>
  familyAccountId + '::' + OrderItemTypeCode.WordwareLunch

export const useWordwareLunchStore = defineStore('wordwareLunch', {
  state: () => ({
    isLoaded: {} as Record<TFamilyAccountId, boolean>,
    balances: {} as Record<TFamilyAccountId, number>
  }),

  getters: {
    isLoading() {
      return (familyAccountId: TFamilyAccountId) => !(this.isLoaded[familyAccountId] ?? false)
    },
    getBalance(state) {
      return (familyAccountId: TFamilyAccountId) => state.balances[familyAccountId] ?? 0
    }
  },

  actions: {
    async fetch(districtId: TDistrictId, familyAccountId: TFamilyAccountId) {
      this.isLoaded[familyAccountId] = false
      const balance = await API.Get.wordwareLunchBalance(familyAccountId)
      this.balances[familyAccountId] = balance

      // A district with no supported merchant account can't accept payments, so
      // don't auto-add an owed balance to a cart the family can never check out
      // (DTB-343). The balance still displays; adding funds is disabled.
      const acceptsPayments = useStore().getDistrict(districtId)?.acceptingPayments !== false

      if (acceptsPayments && -balance > 0.01) {
        console.debug(
          'Auto-adding cart amount of ' + formatCurrency(-balance) + ' to family ' + familyAccountId
        )
        this.addToCart(districtId, familyAccountId, -balance)
      }

      this.validateCart(districtId, familyAccountId)
      this.isLoaded[familyAccountId] = true
    },

    // This function is used to validate the cart lines for the wordware lunch store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, familyAccountId: TFamilyAccountId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TWordwareLunchCartLine[]) {
        if (
          line.orderItemType === OrderItemType.WordwareLunch &&
          line.familyAccountId === familyAccountId
        ) {
          if (line.familyAccountId !== familyAccountId) {
            console.log('family account id mismatch, removing from cart', line.familyAccountId)
            this.removeFromCart(districtId, line.familyAccountId)
          }
        }
      }
    },

    addToCart(districtId: TDistrictId, familyAccountId: number, amount: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(familyAccountId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine && amount < 0.01) {
        this.removeFromCart(districtId, familyAccountId)
        return
      } else if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, {
          amount
        })
      } else {
        const line: TWordwareLunchCartLine = {
          uniqueId,
          familyAccountId,
          description: `Added Lunch Balance to Family Account #${familyAccountId}`,
          amount,
          orderItemType: OrderItemType.WordwareLunch
        }
        cartStore.addCartLine(districtId, uniqueId, line)
      }
    },

    removeFromCart(districtId: TDistrictId, familyAccountId: number) {
      const cartStore = useCartStore()
      const uniqueId = familyAccountId + '::' + OrderItemTypeCode.WordwareLunch
      cartStore.removeCartLine(districtId, uniqueId)
    },

    clearCartForDistrict(districtId: TDistrictId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId)) {
        if (line.orderItemType === OrderItemType.WordwareLunch) {
          this.removeFromCart(districtId, (line as TWordwareLunchCartLine).familyAccountId)
        }
      }
    }
  }
})
